import React from 'react'
import { Typography } from 'react-components'
import { FormattedRule } from '../../../../models/property-details-page/booking-cancellation-policy-model'
import { useTranslation } from '../../../../hooks/locale'
import { useDateFormatter } from '../../../../hooks/date'
import { sub } from 'date-fns'
import isDate from 'lodash/isDate'
import { RuleContainer, Rule, RuleHeader, IconContainer, Icon } from './styles'
import IconMapping from '../../icon-mapping'
import RuleSubText from './rule-sub-text'

interface RuleRendererProps {
    rules: FormattedRule[]
    type: 'fullRefund' | 'noRefund' | 'partialRefund'
    isSlab: boolean
    arrivalDate: Date | null
}

// Threshold to consider a rule as having a very high number of days before arrival
const HIGH_DAYS_TO_THRESHOLD = 400

const isValidDate = (date: unknown): date is Date => isDate(date)

const RuleRenderer: React.FC<RuleRendererProps> = ({ rules, type, isSlab, arrivalDate }) => {
    const { t } = useTranslation()
    const { format } = useDateFormatter()

    const subtractDays = (date: string | Date | null, days: number, dateFormat = 'dd MMM yyyy'): string => {
        if (!date || !isValidDate(new Date(date))) {
            return '--'
        }
        const originalDate = typeof date === 'string' ? new Date(date) : date
        return format(sub(originalDate, { days }), dateFormat)
    }

    const getMessageKey = (rules: FormattedRule[], type: string, isSlab: boolean, daysFrom: number): string => {
        const base = isSlab ? `slab.${type}` : type
        const hasArrivalDate = isValidDate(arrivalDate)
        const firstRule = rules[0]
        const lastRule = rules.at(-1)!

        const isZeroDays = isSlab && type !== 'fullRefund' && firstRule.daysFrom === 0 && firstRule.daysTo === 0
        const isZeroStart = isSlab && type === 'partialRefund' && daysFrom === 0
        const isHighDaysTo = isSlab && type === 'partialRefund' && lastRule.daysTo > HIGH_DAYS_TO_THRESHOLD

        if (isZeroDays) {
            return `bookingCancellationPolicy.${base}.messageZeroDays`
        }
        if (isZeroStart) {
            return `bookingCancellationPolicy.${base}.messageZeroStart`
        }
        if (isHighDaysTo) {
            return hasArrivalDate
                ? `bookingCancellationPolicy.${base}.messageWithArrivalHighDaysTo`
                : `bookingCancellationPolicy.${base}.messageHighDaysTo`
        }

        return hasArrivalDate && isSlab
            ? `bookingCancellationPolicy.${base}.messageWithArrival`
            : `bookingCancellationPolicy.${base}.message`
    }

    const getDateForMessage = (rule: FormattedRule, type: string, isSlab: boolean, isHighDaysTo: boolean): string => {
        if (!isValidDate(arrivalDate)) {
            return '--'
        }
        const daysToSubtract = isHighDaysTo || !(isSlab && type !== 'fullRefund') ? rule.daysFrom : rule.daysTo
        return subtractDays(arrivalDate, daysToSubtract)
    }

    if (rules.length === 0) {
        return null
    }

    const daysFrom = Math.min(...rules.map(r => r.daysFrom))
    const daysTo = Math.max(...rules.map(r => r.daysTo))
    const titleRule = rules[0]
    const lastRule = rules.at(-1)!

    const isHighDaysTo = isSlab && type === 'partialRefund' && lastRule.daysTo > HIGH_DAYS_TO_THRESHOLD
    const messageKey = getMessageKey(rules, type, isSlab, daysFrom)
    const dateForMessage = getDateForMessage(titleRule, type, isSlab, isHighDaysTo)

    return (
        <RuleContainer>
            <Rule>
                <RuleHeader>
                    <IconContainer>
                        <Icon as={IconMapping[titleRule.icon] as any} />
                    </IconContainer>
                    <Typography variant="subtitle2" fontWeight="regular" color={titleRule.color}>
                        {t(messageKey, {
                            daysFrom,
                            daysTo,
                            percent: titleRule.percent,
                            dateFrom: isValidDate(arrivalDate) ? subtractDays(arrivalDate, daysFrom) : '--',
                            dateTo: isValidDate(arrivalDate) ? subtractDays(arrivalDate, daysTo) : '--',
                            date: dateForMessage,
                        })}
                    </Typography>
                </RuleHeader>
                {rules.map((rule, index) => (
                    <RuleSubText
                        key={index}
                        rule={rule}
                        type={type}
                        isSlab={isSlab}
                        index={index}
                        arrivalDate={arrivalDate}
                    />
                ))}
            </Rule>
        </RuleContainer>
    )
}

export default RuleRenderer
